import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashValues from 'lodash/fp/values';

import { USER_ROLE_PARTNER, USER_ROLE_ADMIN, USER_ROLE_LANDLORD } from 'common/userRoles';
import { PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD } from 'common/propertyAttributeConstants';

import { DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS } from 'common/dataTableConstants';

import { SCREEN_ID_PARTNER_MARKET_LISTING_FLOW } from 'client/constants/screenIds';

import { getPartnerSignupType } from 'common/partnerHelpers';

import branch from 'client/core/branch';
import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import PropertyAttributeActions from 'client/actions/PropertyAttribute';
import { getPropertyAttributeListKeyId } from 'client/actions/PropertyAttribute/getList';
import PartnerActions from 'client/actions/Partner';
import NavigatorActions from 'client/actions/Navigator';
import HelpCardActions from 'client/actions/HelpCard';
import MarketActions from 'client/actions/Market';

// eslint-disable-next-line max-len
import SrPartnerMarketListingForm from 'client/components/SrPartnerMarketListingForm/SrPartnerMarketListingForm';

function getMarketingListingDataTableSettings({ match }) {
  const { marketingPartnerListingId, partnerSlug } = match.params;

  if (!marketingPartnerListingId) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingPartnerListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `marketing-partner-listing-id-${marketingPartnerListingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': partnerSlug,
      'marketingPartnerListings.id': parseInt(marketingPartnerListingId, 10),
    },
    sort: {
      'marketingPartnerListings.createdDate': 'desc',
    },
  };
  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_MARKET_LISTING_FLOW)
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER, USER_ROLE_LANDLORD],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  userAgent: ['analytics', 'userAgent'],
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  marketsById: ['markets', 'byId'],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.propertyAttributes = [
      'propertyAttributes',
      'listBy',
      getPropertyAttributeListKeyId({
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      }),
    ];
    cursors.helpCards = [
      'helpCards',
      'list',
      [SCREEN_ID_PARTNER_MARKET_LISTING_FLOW, getPartnerSignupType(partner)].join('-'),
    ];
  }

  return cursors;
})
@dataTable(getMarketingListingDataTableSettings)
class MarketingPartnerListingPage extends Component {
  static fetchData(match, inject) {
    const partnersBySlug = inject.state.get(['partners', 'bySlug']);
    const promises = [MarketActions.getList(inject)];

    if (!partnersBySlug[match.params.partnerSlug]) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    if (match.params.marketingPartnerListingId) {
      const dataTableSettings = getMarketingListingDataTableSettings({ match });
      actionInitializeDataTableState(inject, dataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
    }

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      return Promise.all([
        PropertyAttributeActions.getList(inject, {
          partnerId: partner.id,
          category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
        }),
        HelpCardActions.getList(inject, {
          screenId: SCREEN_ID_PARTNER_MARKET_LISTING_FLOW,
          partnerSignupType: getPartnerSignupType(partner),
        }),
      ]);
    });
  }

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    partner: PropTypes.object,
    propertyAttributes: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    helpCards: PropTypes.array,
  };

  _onPartnerMarketListingFormBack = () => {
    const { partner, dispatch } = this.props;
    dispatch(NavigatorActions.replace, { to: `/partner/${partner.slug}` });
  };

  _onPartnerMarketListingFormSubmit = (marketingPartnerListingId) => {
    const { partner, dispatch, history } = this.props;
    dispatch(PartnerActions.loadPartnerBySlug, {
      slug: partner.slug,
      forceRefresh: true,
    });
    this._reloadPartnerListing();
    dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    });
    history.replace(`/partner/${partner.slug}?newMarketingListingId=${marketingPartnerListingId}`);
  };

  _reloadPartnerListing = () => {
    const { match, dispatch } = this.props;
    const dataTableSettings = getMarketingListingDataTableSettings({ match });
    if (dataTableSettings) {
      return dispatch(actionDataTableLoadItems, dataTableSettings);
    }
  };

  render() {
    const {
      currentUser,
      userAgent,
      partner,
      propertyAttributes,
      dispatch,
      match,
      marketingPartnerListing,
      helpCards,
      marketsById,
    } = this.props;

    if (
      !partner ||
      (match.params.marketingPartnerListingId && !marketingPartnerListing) ||
      !propertyAttributes
    ) {
      return <div className="loading-gif" />;
    }

    return (
      <SrPartnerMarketListingForm
        currentUser={currentUser}
        userAgent={userAgent}
        partnerId={partner.id}
        propertyAttributes={propertyAttributes}
        editMarketingPartnerListing={marketingPartnerListing}
        onBack={this._onPartnerMarketListingFormBack}
        onSubmit={this._onPartnerMarketListingFormSubmit}
        dispatch={dispatch}
        partner={partner}
        helpCards={helpCards}
        marketsList={lodashValues(marketsById)}
      />
    );
  }
}

MarketingPartnerListingPage.displayName = 'MarketingPartnerListingPage';

export default MarketingPartnerListingPage;
